/**
 * Duel / pack clips from `/sfx/{name}.mp3` (ogg/wav also work).
 * Plays through Web Audio so mobile PWA cues stay in time with the board.
 * Returns false when the file is missing or not decoded yet so callers
 * can fall back to the synth (also on-time).
 */

const EXTS = ['mp3', 'ogg', 'wav'] as const

const isAudioType = (type: string): boolean => {
  const t = type.toLowerCase()
  if (t.includes('text/html')) return false
  if (!t) return true
  return (
    t.includes('audio') ||
    t.includes('octet-stream') ||
    t.includes('application/ogg')
  )
}

class SampleBankController {
  private readonly missing = new Set<string>()
  private readonly urls = new Map<string, string>()
  private readonly raw = new Map<string, ArrayBuffer>()
  private readonly buffers = new Map<string, AudioBuffer>()
  private readonly loading = new Map<string, Promise<string | null>>()
  private ctx: AudioContext | null = null
  private dest: AudioNode | null = null
  private mix: GainNode | null = null

  /**
   * Bind the shared AudioContext (call from the first user gesture).
   * Decodes any clips already fetched so the next play() is sample-accurate.
   */
  attach(ctx: AudioContext, dest?: AudioNode | null): void {
    this.ctx = ctx
    if (dest) {
      this.dest = dest
    } else if (!this.mix || this.mix.context !== ctx) {
      this.mix = ctx.createGain()
      this.mix.gain.value = 1
      this.mix.connect(ctx.destination)
      this.dest = this.mix
    }
    if (ctx.state === 'suspended') void ctx.resume()
    for (const name of this.raw.keys()) void this.decode(name)
  }

  /**
   * Resolves a clip URL if the file exists under public/sfx.
   */
  resolve(name: string): Promise<string | null> {
    if (this.missing.has(name)) return Promise.resolve(null)
    const hit = this.urls.get(name)
    if (hit) {
      void this.decode(name)
      return Promise.resolve(hit)
    }
    const pending = this.loading.get(name)
    if (pending !== undefined) return pending
    const job = this.probe(name)
    this.loading.set(name, job)
    return job
  }

  /**
   * Starts every listed clip so the first in-game play is instant.
   */
  preload(names: readonly string[]): void {
    for (const name of names) void this.resolve(name)
  }

  /**
   * Plays a decoded clip on the shared graph. False → use the synth.
   * Never uses HTMLAudioElement: on iOS/PWA that starts late and cuts
   * the previous cue, which is why duels sounded off-time on phones.
   */
  play(name: string, volume = 0.82): boolean {
    if (this.missing.has(name)) return false
    const ctx = this.ctx
    const dest = this.dest
    const buf = this.buffers.get(name)
    if (!ctx || !dest || !buf) {
      void this.resolve(name)
      return false
    }
    if (ctx.state === 'suspended') void ctx.resume()
    try {
      const src = ctx.createBufferSource()
      src.buffer = buf
      const g = ctx.createGain()
      g.gain.value = Math.max(0, Math.min(2, volume))
      src.connect(g)
      g.connect(dest)
      src.onended = () => {
        try {
          src.disconnect()
          g.disconnect()
        } catch {
          /* already disconnected */
        }
      }
      src.start()
      return true
    } catch {
      return false
    }
  }

  private async probe(name: string): Promise<string | null> {
    for (const ext of EXTS) {
      const url = `/sfx/${name}.${ext}`
      try {
        const res = await fetch(url, { cache: 'force-cache' })
        if (!res.ok) continue
        if (!isAudioType(res.headers.get('content-type') || '')) continue
        const data = await res.arrayBuffer()
        this.urls.set(name, url)
        this.raw.set(name, data)
        this.loading.delete(name)
        void this.decode(name)
        return url
      } catch {
        /* try next extension */
      }
    }
    this.missing.add(name)
    this.loading.delete(name)
    return null
  }

  private async decode(name: string): Promise<void> {
    const ctx = this.ctx
    const raw = this.raw.get(name)
    if (!ctx || !raw || this.buffers.has(name)) return
    try {
      const buf = await ctx.decodeAudioData(raw.slice(0))
      this.buffers.set(name, buf)
    } catch {
      /* leave the clip for synth fallback */
    }
  }
}

export const SampleBank = new SampleBankController()
